import Background from "../components/Background";

export default function PrivacyPolicy() {
  return (
    <Background>
      <div className="min-h-screen px-4 py-10">
        <div className="w-full max-w-4xl mx-auto bg-white rounded-3xl border border-gray-200 shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            AYUPATH Privacy Policy
          </h1>
          
          <p className="text-sm text-gray-600 mb-8">
            Effective date: 01/01/2026
          </p>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            <p>
              AYUPATH is a platform that uses a combination of human insight and machine‑learning algorithms 
              to generate personalised life readings based on your birth details. Our services are offered by 
              Power Pilot Pvt Ltd. ("AYUPATH," "we," or "us"). We respect your privacy and are committed to 
              protecting your personal information. This Privacy Policy explains what data we collect, how we 
              use it, the lawful bases for processing, your rights, and how to contact us.
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Why we have a privacy policy
              </h2>
              <p>
                The Australian Privacy Act 1988 and its 13 Australian Privacy Principles (APPs) require 
                organisations that handle personal information to be open and transparent about their data 
                practices[1]. Similarly, the EU General Data Protection Regulation (GDPR) requires businesses 
                processing personal data of European residents to obtain a lawful basis for processing, minimise 
                data collection and provide individuals with rights over their data[2]. AYUPATH's privacy practices 
                are designed to comply with the APPs and applicable international regulations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Personal data we collect
              </h2>
              <p className="mb-4">
                We collect personal data when you use the AYUPATH website, mobile applications or related services 
                (collectively, the "Service"). The information we collect falls into three categories[3]:
              </p>
              <ol className="list-decimal pl-6 space-y-3">
                <li>
                  <strong>Data you provide directly:</strong> When you request a preview, purchase a reading or
                  interact with AYUPATH, you may provide us with your first name, email address, date of birth, place
                  and time of birth, how certain you are of your birth time, what you would like clarity on,
                  payment information, messages, reviews and any other information you choose to give us.
                  After you submit your details, we may show you optional questions asking whether
                  certain past years brought a relationship milestone, the birth of a child, a separation or
                  a harder stretch of life. Answering is optional. Your answers are stored with your request
                  and used to personalise your own preview: it may mention the years you confirmed, and it
                  leaves out topics you said do not apply. Your answers are used to test and improve the
                  accuracy of our readings only if you tick the box allowing it; when used that way, they
                  are analysed without your name or email.
                  If you tick that box, we may also ask, optionally, in which years those events
                  happened. Those years are stored and used the same way.
                  If you buy a full report and tell us your question, the areas you want to focus on or
                  your current situation, we store them with your order and use them only to write your report.
                  If you rate your preview or answer our optional feedback survey, your answers are stored
                  with your request and used only to improve AyuPath. We contact you about them only if
                  you tick the box allowing it.
                  We do not ask for your gender.
                </li>
                <li>
                  <strong>Data we receive from third parties:</strong> If you register or log in via a third‑party 
                  service such as Google or Apple, we may receive certain profile data (for example, your email 
                  address) from that service[3]. We also receive payment confirmation details from our payment 
                  processors.
                </li>
                <li>
                  <strong>Data collected automatically:</strong> When you use the Service, we automatically collect 
                  technical information such as your IP address, device type and model, operating system, language 
                  settings, time zone, unique device identifiers, browser type, usage statistics and log data. Like 
                  many online services, we use cookies, pixels and similar technologies to recognize you, analyse 
                  how you use our Service and personalise advertising. We may also derive approximate location data 
                  from your IP address.
                </li>
              </ol>
              <p className="mt-4">
                We do not knowingly collect personal data from children under 16 years of age. If you are younger 
                than 16, please do not submit any information to us. If we learn that we have collected personal 
                data from a person under 16, we will delete it[4].
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. How we use your personal data (Purposes and lawful bases)
              </h2>
              <p className="mb-4">
                We use your personal data to operate, provide and improve the Service. Under Australian law and 
                the GDPR we must only collect personal information that is reasonably necessary for our functions 
                or activities and must clearly explain how we use it[5]. We rely on one or more of the following 
                lawful bases to process your data:
              </p>
              
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Purpose of processing</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Categories of personal data</th>
                      <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Lawful basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To provide the Service and administer your account</td>
                      <td className="border border-gray-300 px-4 py-2">All data categories, including onboarding and product data</td>
                      <td className="border border-gray-300 px-4 py-2">Performance of a contract with you or your consent[6]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To research, analyse and enhance AYUPATH's performance</td>
                      <td className="border border-gray-300 px-4 py-2">All data categories</td>
                      <td className="border border-gray-300 px-4 py-2">Legitimate interests in improving services[6]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To ensure quality control and security</td>
                      <td className="border border-gray-300 px-4 py-2">Identifiers, onboarding and product data, communications data</td>
                      <td className="border border-gray-300 px-4 py-2">Performance of contract and legitimate interests[7]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To process payments, refunds and manage billing</td>
                      <td className="border border-gray-300 px-4 py-2">Identifiers, payment data, online activity</td>
                      <td className="border border-gray-300 px-4 py-2">Performance of contract and legal obligations[8]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To provide customer support</td>
                      <td className="border border-gray-300 px-4 py-2">All data categories</td>
                      <td className="border border-gray-300 px-4 py-2">Performance of contract and legitimate interests</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To send service communications</td>
                      <td className="border border-gray-300 px-4 py-2">All data categories</td>
                      <td className="border border-gray-300 px-4 py-2">Performance of contract</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To send marketing communications</td>
                      <td className="border border-gray-300 px-4 py-2">Identifiers, onboarding and product data, device data</td>
                      <td className="border border-gray-300 px-4 py-2">Your consent or legitimate interests[9]</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">To comply with legal obligations</td>
                      <td className="border border-gray-300 px-4 py-2">All categories necessary</td>
                      <td className="border border-gray-300 px-4 py-2">Legal obligations[10]</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                Use of AI and deep research feature
              </h3>
              <p>
                AYUPATH uses artificial intelligence and machine‑learning models to generate personalised life 
                readings and to suggest content. We may analyse your birth information, questionnaire answers and 
                de‑identified communications to personalise your experience. The Australian privacy regulator advises 
                that developers must not assume that publicly available data can be used for training AI models and 
                must ensure any personal information used is collected lawfully and with consent[11]. When we use 
                personal information you have provided for secondary purposes such as AI training, we will obtain 
                your consent or ensure the use would be reasonably expected by you[12]. We take steps to avoid 
                including sensitive information in AI training data and use de‑identification techniques where possible.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Cookies and tracking technologies
              </h2>
              <p>
                We use cookies, pixels and similar technologies to collect usage data, recognise you when you visit 
                our site, deliver and measure advertising and improve our services. You can control cookies through 
                your browser settings. Some third‑party services (e.g., Google Analytics, Meta Pixel, TikTok Pixel) 
                may place cookies on your device. These providers may collect information about your visits to AYUPATH 
                and other sites to deliver interest‑based advertisements. We encourage you to review their privacy 
                policies. Our site does not respond to "Do Not Track" signals; check third‑party policies to determine 
                whether they honour such requests[13].
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. With whom we share your personal data
              </h2>
              <p className="mb-4">
                We may share your personal data with third parties to operate and improve the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Service providers and business partners:</strong> We use data hosting providers, analytics 
                  platforms, customer support tools, communication services (e.g., email/SMS providers), AI model 
                  providers, payment processors, advertising networks and marketing partners to run our business. 
                  For example, Meta Pixel, TikTok Pixel and Google Analytics to analyse user interactions[14]. We 
                  enter into data processing agreements requiring these partners to process your data only on our 
                  behalf and follow appropriate security measures.
                </li>
                <li>
                  <strong>Affiliates:</strong> We may share your information with entities in our corporate group to 
                  provide and improve our services, as long as the use of your data is consistent with this policy[15].
                </li>
                <li>
                  <strong>Experts and advisers:</strong> Where you choose to consult with an advisor or expert through 
                  our platform, we share the information you provide in your consultation with that expert. Those experts 
                  are contractually bound to use your information solely for providing the consultation.
                </li>
                <li>
                  <strong>Legal and compliance:</strong> We may disclose your data to law enforcement authorities, 
                  regulators, legal advisors or other parties if required to comply with a legal obligation, protect 
                  the rights or safety of AYUPATH, our users or others, or enforce our terms and policies.
                </li>
                <li>
                  <strong>Business transfers:</strong> If AYUPATH is involved in a merger, acquisition, restructuring, 
                  bankruptcy or asset sale, your data may be transferred to the successor entity, subject to the same 
                  privacy obligations.
                </li>
              </ul>
              <p className="mt-4">
                We do not sell your personal information for monetary consideration. However, we may allow certain 
                advertising networks to collect data via cookies so they can serve you personalised adverts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. International data transfers
              </h2>
              <p>
                AYUPATH operates globally. We may transfer your personal data to countries other than the one in which 
                you reside or where the data were originally collected to provide the Service or for purposes described 
                in this Policy[4]. When we transfer data outside Australia, the European Economic Area (EEA) or other 
                jurisdictions with data‑protection laws, we implement appropriate safeguards such as contractual clauses 
                to ensure that your rights continue to be protected. Under the GDPR's extraterritorial reach, AI systems 
                processing data of EU residents must meet cross‑border transfer requirements and maintain adequate 
                security measures[16].
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Your rights and choices
              </h2>
              <p className="mb-4">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Access and copy:</strong> You can access and review the personal data we hold about you. 
                  AYUPATH offers this functionality within your account settings or upon request.
                </li>
                <li>
                  <strong>Rectification:</strong> You may correct or update inaccurate or incomplete personal data[15].
                </li>
                <li>
                  <strong>Deletion:</strong> You can request that we delete your personal data. We will take reasonable 
                  steps to honour your request, except where we need to retain certain data to comply with legal 
                  obligations or resolve disputes[15]. Note that even after deletion requests, certain transaction or 
                  compliance data may be stored as required by law[13].
                </li>
                <li>
                  <strong>Object or restrict processing:</strong> You may ask us to stop using or to limit our use of 
                  some or all of your personal data[15]. This may affect your ability to use certain features.
                </li>
                <li>
                  <strong>Data portability:</strong> If you are based in the EEA or another jurisdiction with similar 
                  rights, you can request to receive your personal data in a structured, machine‑readable format[4].
                </li>
                <li>
                  <strong>Complaints to supervisory authorities:</strong> If you reside in the EEA, you have the right 
                  to lodge a complaint with your local data protection authority[4]. We encourage you to contact us 
                  first so we can address your concerns.
                </li>
              </ul>
              <p className="mt-4">
                You can exercise these rights by contacting us (see Section 10). We may need to verify your identity 
                before processing your request and may charge a reasonable fee if permitted by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Security
              </h2>
              <p>
                We implement administrative, technical and physical security measures designed to protect your personal 
                data from unauthorised access, disclosure or loss. These measures include encryption, access controls, 
                network security, staff training and secure development practices. We also require our service providers 
                to implement appropriate security measures. Nonetheless, no method of transmission or storage is entirely 
                secure; you use the Service at your own risk.
              </p>
              <p className="mt-4">
                The Australian regulator emphasises that developers of generative AI models should take a 'privacy by 
                design' approach, conduct privacy impact assessments and ensure that high‑quality data and appropriate 
                testing are used to minimise inaccuracies[17]. AYUPATH follows these principles and clearly labels 
                AI‑generated content as informational rather than determinative.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Data retention
              </h2>
              <p>
                We retain your personal data only as long as necessary to fulfil the purposes described in this Policy 
                and comply with our legal obligations[13]. This typically means we keep your data for the duration of 
                your relationship with us and for a reasonable period thereafter (for example, to respond to legal 
                requests, maintain accounting records or enforce our agreements). We may retain de‑identified or 
                aggregated data for research and statistical purposes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Age limitation
              </h2>
              <p>
                AYUPATH does not knowingly collect or process personal data from individuals under 16 years of age. 
                If you become aware that a child under 16 has provided us with personal data, please contact us so we 
                can delete the data[4].
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Changes to this Privacy Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable 
                laws. When we make material changes, we will notify you by email, through the Service or as otherwise 
                required by law. Your continued use of the Service after the update constitutes your acceptance of the 
                revised policy. The "Effective date" at the top of this document indicates when this policy was last 
                revised.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Contact us
              </h2>
              <p className="mb-3">
                If you have questions about this Privacy Policy or wish to exercise your rights, you can contact 
                AYUPATH via:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:ayupathai@outlook.com" className="text-orange-600 hover:underline">
                    ayupathai@outlook.com
                  </a>
                </li>
                <li>
                  <strong>Address:</strong> 37 Fairfield Crescent, Diggers Rest, VIC 3427 Australia
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </Background>
  );
}
